// PubMed E-utilities client.
//
// Two-step: esearch (PMIDs) then esummary (records). Optional NCBI API key
// raises the rate limit to 10 req/s; without it, 3 req/s.

import { fetchJson } from './http';
import type { PubMedRecord, Result } from './types';

const ESEARCH = 'https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esearch.fcgi';
const ESUMMARY = 'https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esummary.fcgi';

export interface PubMedOptions {
  /** Optional NCBI API key for higher rate limits. */
  apiKey?: string;
  /** Default 20; PubMed caps at much higher but we keep responses small. */
  retmax?: number;
  /** Year filter low bound, e.g. 2020. */
  fromYear?: number;
}

interface SearchEnvelope {
  esearchresult?: { idlist?: string[]; count?: string };
}

interface SummaryEnvelope {
  result?: Record<string, RawSummary | string[]>;
}

interface RawSummaryAuthor {
  name?: string;
  authtype?: string;
}

interface RawSummary {
  uid?: string;
  title?: string;
  pubdate?: string;
  source?: string;
  authors?: RawSummaryAuthor[];
  articleids?: Array<{ idtype?: string; value?: string }>;
}

function pickDoi(raw: RawSummary): string | null {
  for (const a of raw.articleids ?? []) {
    if (a.idtype?.toLowerCase() === 'doi' && a.value) return a.value;
  }
  return null;
}

function parseYear(pubdate?: string): number | null {
  if (!pubdate) return null;
  const m = pubdate.match(/\b(19|20)\d{2}\b/);
  return m ? Number.parseInt(m[0], 10) : null;
}

function parseSummary(raw: RawSummary): PubMedRecord {
  return {
    pmid: raw.uid ?? '',
    title: raw.title ?? '',
    year: parseYear(raw.pubdate),
    journal: raw.source ?? null,
    authors: (raw.authors ?? []).map((a) => ({ name: a.name ?? '', affiliation: null })),
    doi: pickDoi(raw),
  };
}

export async function searchPubmed(
  query: string,
  opts: PubMedOptions = {},
): Promise<Result<PubMedRecord[]>> {
  const retmax = Math.min(Math.max(opts.retmax ?? 20, 1), 100);
  const fromYear = opts.fromYear ?? new Date().getFullYear() - 5;

  // Step 1: esearch.
  const term = `${query} AND (${fromYear}:3000[pdat])`;
  const searchUrl = new URL(ESEARCH);
  searchUrl.searchParams.set('db', 'pubmed');
  searchUrl.searchParams.set('term', term);
  searchUrl.searchParams.set('retmode', 'json');
  searchUrl.searchParams.set('retmax', String(retmax));
  if (opts.apiKey) searchUrl.searchParams.set('api_key', opts.apiKey);

  const searchRes = await fetchJson(searchUrl.toString());
  if (!searchRes.ok) return searchRes;
  const idlist = (searchRes.data as SearchEnvelope).esearchresult?.idlist ?? [];
  if (idlist.length === 0) return { ok: true, data: [] };

  // Step 2: esummary.
  const summaryUrl = new URL(ESUMMARY);
  summaryUrl.searchParams.set('db', 'pubmed');
  summaryUrl.searchParams.set('id', idlist.join(','));
  summaryUrl.searchParams.set('retmode', 'json');
  if (opts.apiKey) summaryUrl.searchParams.set('api_key', opts.apiKey);

  const summaryRes = await fetchJson(summaryUrl.toString());
  if (!summaryRes.ok) return summaryRes;
  const result = (summaryRes.data as SummaryEnvelope).result;
  if (!result) {
    return {
      ok: false,
      degraded: true,
      error: { kind: 'invalid_response', host: 'eutils.ncbi.nlm.nih.gov', message: 'esummary missing result' },
    };
  }

  const records: PubMedRecord[] = [];
  for (const pmid of idlist) {
    const raw = result[pmid];
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      records.push(parseSummary(raw as RawSummary));
    }
  }
  return { ok: true, data: records };
}
